import { TFunction } from "i18next";

import {
  InvoiceMaterialGroup,
  RowInvoice,
  InvoiceMaterial,
  InvoiceTransactionType,
} from "../../../types";
import { formatWeight, priceToFormat } from "../../../helpers/invoice";
import { InvoiceFee } from "../../../types/invoice";
import formatSpecialFee from "../../../helpers/formatSpecialFee";

export const getInvoiceInfoMaterialRows = (
  materials: InvoiceMaterial[],
  transactionType: InvoiceTransactionType,
  isLastGroup?: boolean
): RowInvoice[] => {
  return materials.map((i, materialIdx) => {
    return {
      key: `${transactionType}-${i.materialCode}`,
      name: i.materialName,
      value: formatWeight(i.units.unitQuantity),
      rate: priceToFormat(i.rate),
      additionalValue: priceToFormat(i.price!),
      [isLastGroup ? "withBoldBorder" : "withBorder"]:
        materialIdx === materials.length - 1,
    };
  });
};

export const getInvoiceInfoRows = (
  materialGroup: InvoiceMaterialGroup,
  isLastGroup?: boolean
): RowInvoice[] => {
  const { materials, transactionType } = materialGroup;

  return getInvoiceInfoMaterialRows(materials, transactionType, isLastGroup);
};

export const getDeliveryMaterialsInvoiceInfoRows = (
  materials: InvoiceMaterial[],
  transactionType: InvoiceTransactionType,
  t: TFunction,
  isLastGroup?: boolean,
  keyPrefix: string = ""
): RowInvoice[] => {
  const materialsByRoute = materials
    .filter((m) => m.sourceName)
    .reduce((result, material) => {
      const route = t("invoices.deliveryFromTo", {
        from: material.sourceName,
        to: material.destinationName || t("invoices.otherDestination"),
      });

      if (result[route]) {
        result[route].push(material);
      } else {
        result[route] = [material];
      }

      return result;
    }, {} as { [key: string]: InvoiceMaterial[] });

  const entries = Object.entries(materialsByRoute);

  return entries
    .map(([name, routeMaterials], routeIdx) => [
      {
        name,
        value: "",
        isSubtotal: true,
        key: `${keyPrefix}${transactionType}-${name}`,
      },
      ...routeMaterials.map((m, materialIdx) => ({
        key: `${keyPrefix}${transactionType}-${name}-${m.materialCode}`,
        name: m.materialName,
        value: formatWeight(m.units.unitQuantity),
        rate: priceToFormat(m.rate),
        additionalValue: priceToFormat(m.price!),
        [isLastGroup && routeIdx === entries.length - 1
          ? "withBoldBorder"
          : "withBorder"]: materialIdx === routeMaterials.length - 1,
      })),
    ])
    .flat();
};

export const getDeliveryInvoiceInfoRows = (
  materialGroup: InvoiceMaterialGroup,
  t: TFunction,
  isLastGroup?: boolean
): RowInvoice[] => {
  const { materials, transactionType } = materialGroup;

  return getDeliveryMaterialsInvoiceInfoRows(
    materials,
    transactionType,
    t,
    isLastGroup
  );
};

export const getTransactionFeeRows = (
  transactionFees: InvoiceFee[],
  t: TFunction,
  keyPrefix: string = ""
): RowInvoice[] => {
  if (!transactionFees || transactionFees.length === 0) return [];

  return [
    {
      key: `${keyPrefix}transactionFeesTitle`,
      name: t("invoices.specialFee"),
      value: "",
      isTotal: true,
    },
    ...transactionFees.map((fee: InvoiceFee, index) => ({
      name: fee.feeName,
      key: `${keyPrefix}${fee.specialFeeId}`,
      value: formatSpecialFee(fee),
      rate: priceToFormat(fee.calculatedFee),
      isRedValue: fee.calculatedFee < 0,
      withBorder: index === transactionFees.length - 1,
    })),
  ];
};
